import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON, formatDateWithOrdinal } from '@/utils/dateHelpers'

import { Alert, Link, Text } from '@/ui'
import {
  selectActionableQTEChecklistItemsComplete,
  selectAllQTEChecklistItemsComplete,
  selectUserCreatedPastQteCutoff,
} from './userTaxEstimates.selector'
import { getCurrentUser, getFreeTrialStatus } from '@/selectors/user.selectors'
import { TrialStatus } from '@/reducers/auth/userReducer'
import {
  FETCH_TAX_ESTIMATES_KEY,
  fetchUserTaxEstimatesIfNeeded,
} from './userTaxEstimates.slice'
import {
  FETCH_BOOKKEEPING_REPORT_KEY,
  fetchBookkeepingReports,
} from '@/actions/bookkeepingReportActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'

import {
  selectActiveQTEDetailsAsDates,
  selectIsSameOrAfterChecklistLaunchDate,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import {
  FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
  fetchAllQuarterlyTaxEstimateDetailsIfNeeded,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'

export const useQTEReminderBanner = () => {
  const activeQuarter = useReselector(selectActiveQTEDetailsAsDates)
  const userJoinedTooLate = useReselector(selectUserCreatedPastQteCutoff)
  const checklistComplete = useReselector(
    selectAllQTEChecklistItemsComplete,
    activeQuarter?.taxYear
  )
  // Distinguishes "you still have something to do" from "you've done your part
  // and we're finishing your books". The second must not be told to hurry.
  const actionableItemsComplete = useReselector(
    selectActionableQTEChecklistItemsComplete,
    activeQuarter?.taxYear
  )
  const alwaysOnQteEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.alwaysOnQteEnabled,
    false
  )
  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )
  const checklistAvailable = useReselector(
    selectIsSameOrAfterChecklistLaunchDate
  )
  const trialStatus = useReselector(getFreeTrialStatus)
  const hasActiveTrial = trialStatus === TrialStatus.active
  const isAdminUser = useReselector(getCurrentUser)?.admin
  const isFetching = useReselector(selectIsFetchingForKeys, [
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
    FETCH_TAX_ESTIMATES_KEY,
    FETCH_BOOKKEEPING_REPORT_KEY,
  ])
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
    dispatch(fetchUserTaxEstimatesIfNeeded())
    dispatch(fetchBookkeepingReports())
  }, [dispatch])

  // Always-on keeps the quarter open past its IRS date, so the reminder keeps
  // running rather than going quiet. What changes is what it says.
  const pastIrsPaymentDue = Boolean(
    activeQuarter && activeQuarter.now > activeQuarter.withOffset.irsPaymentDue
  )
  const inLatePaymentWindow = alwaysOnQteEnabled && pastIrsPaymentDue

  // The exception to the reminder running: past the due date with only books
  // outstanding, the user has nothing to act on and the lateness is ours, so a
  // nudge would just be news they cannot use.
  const blockedOnOurBookkeeping = inLatePaymentWindow && actionableItemsComplete

  const shouldShowBanner = !(
    isFetching ||
    userJoinedTooLate ||
    checklistComplete ||
    !hasQTEPermission ||
    hasActiveTrial ||
    isAdminUser ||
    blockedOnOurBookkeeping
  )

  return {
    shouldShowBanner,
    activeQuarter,
    checklistAvailable,
    inLatePaymentWindow,
  }
}

export const QTEReminderBanner = () => {
  const navigate = useNavigate()
  const {
    shouldShowBanner,
    activeQuarter,
    checklistAvailable,
    inLatePaymentWindow,
  } = useQTEReminderBanner()
  const track = useAnalyticsTrack()

  const irsPaymentDue = activeQuarter?.forDisplay.irsPaymentDue
  const checklistDueDate = activeQuarter?.forDisplay.clientNotified
  const checklistLaunchDate = activeQuarter?.forDisplay.checklistLaunchDate

  const title = useMemo(() => {
    if (!checklistAvailable) {
      return `Your Q${activeQuarter?.taxQuarter} ${activeQuarter?.taxYear} tax checklist will be available on ${checklistLaunchDate && formatDateWithOrdinal(checklistLaunchDate, DATE_FORMATS_LUXON.MONTH_DAY_LONG)}.`
    }
    if (inLatePaymentWindow) {
      const dueDate =
        irsPaymentDue &&
        formatDateWithOrdinal(irsPaymentDue, DATE_FORMATS_LUXON.MONTH_DAY_LONG)

      return `Your Q${activeQuarter?.taxQuarter} payment was due ${dueDate}.`
    }
    if (checklistDueDate && DateTime.now() > checklistDueDate) {
      return `Reminder: Quarterly tax payments are due on ${irsPaymentDue && formatDateWithOrdinal(irsPaymentDue, DATE_FORMATS_LUXON.MONTH_DAY_LONG)}.`
    }
    return `Complete your checklist by ${checklistDueDate && formatDateWithOrdinal(checklistDueDate, DATE_FORMATS_LUXON.MONTH_DAY_LONG)} to receive your quarterly tax estimate.`
  }, [
    checklistAvailable,
    checklistDueDate,
    checklistLaunchDate,
    inLatePaymentWindow,
    irsPaymentDue,
    activeQuarter?.taxQuarter,
    activeQuarter?.taxYear,
  ])

  const description = useMemo(() => {
    if (!checklistAvailable) {
      return `Your payment is due on ${irsPaymentDue && formatDateWithOrdinal(irsPaymentDue, DATE_FORMATS_LUXON.MONTH_DAY_LONG)}.`
    }
    // The penalty is named because finishing the checklist is what limits it. The
    // reader who cannot act never gets here — the hook hides the banner for them.
    // No support link: the article it would point at is about calculating safe
    // harbor, which is beside the point while a real estimate is still coming.
    if (inLatePaymentWindow) {
      return 'Finish your checklist to receive your estimate and minimize penalties.'
    }
    if (checklistDueDate && DateTime.now() > checklistDueDate) {
      return (
        <>
          Missed the deadline to get your estimate? Calculate your safe harbor
          amount to avoid penalties.{' '}
          {/* The slug carried a literal "[…]" from a truncated paste, so the URL
              404s. Zendesk resolves an article from its id alone. */}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/25502898092183"
            newPage
          >
            Learn more →
          </Link>
        </>
      )
    }
    return `Your payment is due on ${irsPaymentDue && formatDateWithOrdinal(irsPaymentDue, DATE_FORMATS_LUXON.MONTH_DAY_LONG)}.`
  }, [checklistAvailable, checklistDueDate, inLatePaymentWindow, irsPaymentDue])

  if (!shouldShowBanner) {
    return null
  }
  const permanentDismissalKey = `q${activeQuarter?.taxQuarter}-${activeQuarter?.taxYear}-qte-banner`

  return (
    <Alert
      variant="default"
      title={title}
      dismissible
      permanentDismissalKey={permanentDismissalKey}
      ctaOnClick={
        checklistAvailable
          ? () => {
              track('qte_banner_start_clicked')
              navigate('/taxes/quarterly')
            }
          : undefined
      }
      ctaText={checklistAvailable ? 'Start' : undefined}
      ctaProps={{
        variant: 'primary',
        size: 'md',
      }}
    >
      <Text variant={TypographyStyles.BODY_SM}>{description}</Text>
    </Alert>
  )
}
